import { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ant design icons
import DisconnectOutlined from '@ant-design/icons/DisconnectOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import PauseCircleOutlined from '@ant-design/icons/PauseCircleOutlined';
import EnvironmentOutlined from '@ant-design/icons/EnvironmentOutlined';

// project imports
import MainCard from 'components/MainCard';
import useDatasheetHistory, { historyDateKey } from 'hooks/useDatasheetHistory';
import BehaviorTrackingTable from './BehaviorTrackingTable';
import { CSVExport } from 'components/third-party/react-table';
import { openSnackbar } from 'api/snackbar';
import useOnlineStatus from 'hooks/useOnlineStatus';
// Shared with the Behavior Analysis report so the dropdown options and the
// report's settings come from the exact same parser (single source of truth).
import { getUniqueBehaviorSettings } from 'utils/behaviorSettings';

// ant design icons for offline
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import SpeechTextField from 'components/SpeechTextField';

const EMPTY_BEHAVIORS = [];

/**
 * HOW MANY TIMES DID THIS BEHAVIOUR HAPPEN?
 *
 * A tally press is one occurrence — and so is a timed episode, once its timer has been
 * stopped. Pressing Timer, letting it run and pressing Stop records BOTH that the
 * behaviour happened and how long it lasted; only the second half was being kept.
 * A session with three ten-minute episodes therefore read as "0 occurrences", and
 * because frequencyCount is the sole source of truth for the behaviour reports, those
 * episodes were invisible there too.
 *
 * A RUNNING timer is deliberately excluded. The episode is not over yet, and
 * totalDuration already ignores it for exactly that reason — so the count and the
 * duration always describe the same set of episodes, and the number ticks up when the
 * therapist presses Stop rather than when they press Start.
 *
 * Used by the Individual card, the saved payload and the Grid row alike: three copies
 * of this rule had already been written out by hand, which is how they came to disagree.
 */
const countEpisodes = (occurrences, matches) => occurrences.filter((o) => !o.active && matches(o)).length;

const BehaviorTrackingMode = ({
  viewMode = 'individual',
  onDataChange,
  behaviors = EMPTY_BEHAVIORS,
  // Offline mode props
  isOfflineMode = false,
  childId,
  childName,
  therapistId,
  therapistName,
  sessionId,
  onSave,
  onBack,
  // Parent callback: invoked with `{ behaviorName, elapsed, stop }` whenever
  // a duration timer is active, or `null` when no timer is running. Lets the
  // parent surface a persistent "recording" chip on other tabs.
  onTimerStateChange
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Get actual network status - auto-detect offline regardless of isOfflineMode prop
  const { isOnline } = useOnlineStatus();

  // Use configuration or defaults
  const activeBehaviors = behaviors;

  // Union of IEP-defined settings across all tracked behaviors. Drives the
  // "Setting / Context" dropdown so therapists record WHERE each incident
  // occurred. Sourced from the same shared helper the Behavior Analysis report
  // uses, so the two lists can never drift apart.
  const availableSettings = useMemo(() => getUniqueBehaviorSettings(activeBehaviors), [activeBehaviors]);

  // A setting must be chosen before recording — but only when the IEP actually
  // defines settings for these behaviors (otherwise tracking would be blocked).
  const settingRequired = availableSettings.length > 0;
  const [selectedSetting, setSelectedSetting] = useState('');

  // Default the setting once options are available: prefer a "therapy"-type
  // setting (this is a therapy session), else fall back to the first option.
  useEffect(() => {
    if (!selectedSetting && availableSettings.length > 0) {
      const preferred = availableSettings.find((s) => /therapy/i.test(s)) || availableSettings[0];
      setSelectedSetting(preferred);
    }
  }, [availableSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  const [behaviorOccurrences, setBehaviorOccurrences] = useState([]);
  const [activeDuration, setActiveDuration] = useState(null);
  const [durationTimer, setDurationTimer] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [sessionNotes, setSessionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedBreakdown, setExpandedBreakdown] = useState(null); // Track which behavior's breakdown is expanded
  // Per-behavior "function" attribute (Attention / Auto / Escape) — only edited
  // in Grid view, kept in a side map so Individual mode (which doesn't surface
  // it) can ignore it.
  const [behaviorFunctions, setBehaviorFunctions] = useState({});
  // Grid sub-rows with NO OCCURRENCE BEHIND THEM YET - either no behavior chosen,
  // or a behavior chosen but nothing recorded against it. Held separately because
  // today's row is derived from behaviorOccurrences, so a row with no occurrence
  // cannot be derived and would vanish on the next render. This is what lets a
  // therapist add a row, name it, and only then type the count.
  const [pendingGridRows, setPendingGridRows] = useState([]);

  // Ref to skip auto-save on initial render
  const initialRenderRef = useRef(true);

  // Auto-save: notify parent when form data changes for debounced save to IndexedDB → PostgreSQL
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Skip if no data has been entered
    const completedOccurrences = behaviorOccurrences.filter((o) => !o.active);
    if (completedOccurrences.length === 0 && !sessionNotes) {
      return;
    }

    const formData = {
      behaviors: activeBehaviors.map((behavior) => ({
        behaviorId: behavior.id,
        behaviorName: behavior.name,
        frequencyCount: countEpisodes(behaviorOccurrences, (o) => o.behaviorId === behavior.id),
        totalDuration: behaviorOccurrences
          .filter((o) => o.behaviorId === behavior.id && o.type === 'duration' && !o.active)
          .reduce((sum, o) => sum + (o.duration || 0), 0),
        function: behaviorFunctions[behavior.name] || ''
      })),
      occurrences: completedOccurrences,
      totalOccurrences: completedOccurrences.length,
      behaviorFunctions,
      setting: selectedSetting || null,
      sessionNotes
    };

    if (onDataChange) onDataChange('behavior-tracking', formData);
  }, [behaviorOccurrences, behaviorFunctions, selectedSetting, sessionNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get total occurrences count
  const getTotalOccurrences = () => behaviorOccurrences.filter((o) => !o.active).length;

  // Handle save - uses offline-first pattern (always saves to IndexedDB, syncs in background)
  const handleSave = async (showNotification = true) => {
    setSaving(true);
    try {
      // Don't save if no data
      if (getTotalOccurrences() === 0) {
        setSaving(false);
        return;
      }

      const formData = {
        behaviors: activeBehaviors.map((behavior) => ({
          behaviorId: behavior.id,
          behaviorName: behavior.name,
          frequencyCount: getFrequencyCount(behavior.id),
          totalDuration: getTotalDuration(behavior.id),
          function: behaviorFunctions[behavior.name] || ''
        })),
        occurrences: behaviorOccurrences.filter((o) => !o.active),
        totalOccurrences: getTotalOccurrences(),
        behaviorFunctions,
        setting: selectedSetting || null,
        sessionNotes
      };

      // Notify parent to persist via its upsert pattern (persistToIndexedDB)
      if (onDataChange) {
        onDataChange('behavior-tracking', formData);
      }

      if (showNotification) {
        openSnackbar({
          open: true,
          message: isOnline
            ? 'Behavior Tracking data saved! Will sync at session end.'
            : 'Behavior Tracking data saved locally. Will sync when online.',
          variant: 'alert',
          alert: { color: 'success' }
        });
      }

      if (onSave) onSave();
      if (isOfflineMode && onBack) onBack();
    } catch (error) {
      console.error('Error saving entry:', error);
      if (showNotification) {
        openSnackbar({
          open: true,
          message: 'Failed to save data. Please try again.',
          variant: 'alert',
          alert: { color: 'error' }
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // Alias for backward compatibility with existing UI buttons
  const handleOfflineSave = handleSave;

  // Helper function to check if a date string is today
  const isToday = (dateString) => {
    const today = new Date();
    const checkDate = new Date(dateString);
    return (
      checkDate.getDate() === today.getDate() &&
      checkDate.getMonth() === today.getMonth() &&
      checkDate.getFullYear() === today.getFullYear()
    );
  };

  /* Local edits to non-today rows. The comment here used to claim this was
   * "populated from API" — nothing ever did, so the Historical tab showed today's
   * derived row and nothing else. Saved entries are loaded below; this now holds
   * only in-memory overrides layered on top of them. */
  const [behaviorTrackingData, setBehaviorTrackingData] = useState([]);

  /* How far back the Historical tab loads. The table's own date pickers filter
   * within this window. */
  const historyWindow = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: iso(start), end: iso(end) };
  }, []);

  /* Range-bounded rather than paged: the table filters by date across the whole
   * window, so a page of five would hide days that do have data. */
  const { records: historyRecords } = useDatasheetHistory(childId, 'behavior_frequency', {
    startDate: historyWindow.start,
    endDate: historyWindow.end
  });

  /* One grid row per saved session: a date with its per-behaviour tallies. */
  const savedTrackingRows = useMemo(
    () =>
      historyRecords
        .map((record) => {
          const rd = record.raw_data || {};
          const key = historyDateKey(record);
          if (!key || !Array.isArray(rd.behaviors)) return null;
          const [y, m, d] = key.split('-').map(Number);
          if (!y || !m || !d) return null;
          const subRows = rd.behaviors.map((b) => ({
            behavior: b.behaviorName || '',
            function: b.function || '',
            count: b.frequencyCount || 0,
            duration: b.totalDuration || 0
          }));
          return {
            // Must match formatDateString() below exactly — the tab compares this
            // string to decide which row is today's. Inlined because that helper is
            // declared further down and would be in its temporal dead zone here.
            date: new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            count: subRows.reduce((s, r) => s + r.count, 0),
            duration: subRows.reduce((s, r) => s + r.duration, 0),
            subRows
          };
        })
        .filter(Boolean),
    [historyRecords]
  );

  /* Saved rows plus local overrides, newest day first. A day edited in this session
   * replaces its saved copy rather than appearing twice. */
  const historicalRows = useMemo(() => {
    const byDate = new Map();
    savedTrackingRows.forEach((r) => byDate.set(r.date, r));
    behaviorTrackingData.forEach((r) => byDate.set(r.date, r));
    return [...byDate.values()].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [savedTrackingRows, behaviorTrackingData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFrequencyClick = (behavior) => {
    const timestamp = new Date();
    const newOccurrence = {
      id: Date.now(),
      behaviorId: behavior.id,
      behaviorName: behavior.name,
      severity: behavior.severity,
      type: 'frequency',
      timestamp: timestamp,
      time: timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      duration: null,
      setting: selectedSetting || null,
      notes: ''
    };
    setBehaviorOccurrences([newOccurrence, ...behaviorOccurrences]);

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const handleStartDuration = (behavior) => {
    const timestamp = new Date();
    const newOccurrence = {
      id: Date.now(),
      behaviorId: behavior.id,
      behaviorName: behavior.name,
      severity: behavior.severity,
      type: 'duration',
      timestamp: timestamp,
      time: timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      duration: 0,
      setting: selectedSetting || null,
      notes: '',
      active: true
    };
    setBehaviorOccurrences([newOccurrence, ...behaviorOccurrences]);
    setActiveDuration(newOccurrence.id);
    setDurationTimer(0);

    // Start timer
    const interval = setInterval(() => {
      setDurationTimer((prev) => prev + 1);
    }, 1000);

    // Store interval ID
    newOccurrence.intervalId = interval;

    if (onDataChange) {
      onDataChange();
    }
  };

  const handleStopDuration = (occurrenceId) => {
    setBehaviorOccurrences(
      behaviorOccurrences.map((occ) => {
        if (occ.id === occurrenceId) {
          clearInterval(occ.intervalId);
          return { ...occ, duration: durationTimer, active: false };
        }
        return occ;
      })
    );
    setActiveDuration(null);
    setDurationTimer(0);

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const handleRemoveOccurrence = (id) => {
    const occurrence = behaviorOccurrences.find((o) => o.id === id);
    if (occurrence?.intervalId) {
      clearInterval(occurrence.intervalId);
    }
    setBehaviorOccurrences(behaviorOccurrences.filter((o) => o.id !== id));
    if (activeDuration === id) {
      setActiveDuration(null);
      setDurationTimer(0);
    }

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const handleUpdateNotes = (id, notes) => {
    setBehaviorOccurrences(behaviorOccurrences.map((o) => (o.id === id ? { ...o, notes } : o)));

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const getFrequencyCount = (behaviorId) => countEpisodes(behaviorOccurrences, (o) => o.behaviorId === behaviorId);

  const getTotalDuration = (behaviorId) => {
    const durations = behaviorOccurrences.filter((o) => o.behaviorId === behaviorId && o.type === 'duration' && !o.active);
    return durations.reduce((sum, o) => sum + (o.duration || 0), 0);
  };

  // Get all duration intervals for a behavior
  const getDurationIntervals = (behaviorId) => {
    return behaviorOccurrences.filter((o) => o.behaviorId === behaviorId && o.type === 'duration' && !o.active);
  };

  // Check if this behavior has an active timer
  const isTimerActive = (behaviorId) => {
    return activeDuration && behaviorOccurrences.find((o) => o.id === activeDuration)?.behaviorId === behaviorId;
  };

  // Always keep the latest stop-handler in a ref so the callback handed to the
  // parent never becomes stale across renders.
  const stopHandlerRef = useRef(() => {});
  stopHandlerRef.current = () => {
    if (activeDuration) handleStopDuration(activeDuration);
  };

  // Report active-timer state to the parent so it can render a persistent
  // "recording" chip in the tab strip while the therapist works in other tabs.
  useEffect(() => {
    if (!onTimerStateChange) return;
    if (activeDuration === null) {
      onTimerStateChange(null);
      return;
    }
    const occ = behaviorOccurrences.find((o) => o.id === activeDuration);
    if (!occ) return;
    onTimerStateChange({
      behaviorName: occ.behaviorName,
      elapsed: durationTimer,
      stop: () => stopHandlerRef.current?.()
    });
    // onTimerStateChange intentionally excluded — parent memoizes it; including
    // would re-fire the effect when parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDuration, durationTimer, behaviorOccurrences]);

  // Clear parent's chip when this component unmounts (defensive — the parent
  // keeps it mounted, but a future refactor might not).
  useEffect(() => {
    return () => {
      if (onTimerStateChange) onTimerStateChange(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateString = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Today's grid row is DERIVED live from behaviorOccurrences + behaviorFunctions
  // + pendingGridRows. This is what makes Individual ↔ Grid stay in sync.
  const computeTodayRow = () => {
    const todayLabel = formatDateString(new Date());
    const completed = behaviorOccurrences.filter((o) => !o.active);
    const byBehavior = new Map();
    for (const occ of completed) {
      if (!byBehavior.has(occ.behaviorName)) {
        byBehavior.set(occ.behaviorName, {
          behavior: occ.behaviorName,
          function: behaviorFunctions[occ.behaviorName] || '',
          count: 0,
          duration: 0
        });
      }
      const entry = byBehavior.get(occ.behaviorName);
      // Every finished occurrence counts, timed or tallied (see countEpisodes). The loop
      // above already filtered to completed ones, so a running timer is excluded here too.
      entry.count += 1;
      if (occ.type === 'duration') entry.duration += occ.duration || 0;
    }
    // Pending rows are appended, but only where the occurrence log has not
    // already produced that behaviour: once a row earns an occurrence it is
    // derived, and showing the parked copy as well would put the same behaviour
    // in the grid twice with different numbers.
    const derived = Array.from(byBehavior.values());
    const pendingToShow = pendingGridRows.filter((r) => !r.behavior || !byBehavior.has(r.behavior));
    const subRows = [...derived, ...pendingToShow];
    return {
      date: todayLabel,
      count: subRows.reduce((s, r) => s + (r.count || 0), 0),
      duration: subRows.reduce((s, r) => s + (r.duration || 0), 0),
      subRows
    };
  };

  // Translate a grid-side update of today's row back into mutations of the
  // canonical occurrence list + functions map + pending-row buffer.
  //   • count delta → add/remove synthetic frequency occurrences
  //   • duration change → replace today's duration intervals for that behavior
  //     with a single synthetic interval of the new total
  //   • behavior renamed on a row → old behavior's occurrences drop, new
  //     behavior's count/duration are seeded
  //   • rows with nothing recorded yet (no behavior, or named with count and
  //     duration both zero) → kept in pendingGridRows so they stay on screen
  // Active duration timers are preserved (never reconciled).
  const reconcileTodayRow = (newTodayRow) => {
    const newSubRows = newTodayRow?.subRows || [];
    const namedSubRows = newSubRows.filter((sr) => sr.behavior);
    const newBehaviorSet = new Set(namedSubRows.map((sr) => sr.behavior));

    // Drop completed occurrences for behaviors no longer represented in the
    // grid. Keep active timers regardless.
    let nextOccurrences = behaviorOccurrences.filter((o) => o.active || newBehaviorSet.has(o.behaviorName));

    const nextFunctions = {};
    const nowIso = new Date();
    const timeLabel = nowIso.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    let idCounter = Date.now();

    for (const sr of namedSubRows) {
      const behName = sr.behavior;
      nextFunctions[behName] = sr.function || '';

      const behConfig = activeBehaviors.find((b) => b.name === behName);
      const targetCount = Math.max(0, Number(sr.count) || 0);
      const targetDuration = Math.max(0, Number(sr.duration) || 0);

      const existingForB = nextOccurrences.filter((o) => !o.active && o.behaviorName === behName);
      const existingFreq = existingForB.filter((o) => o.type === 'frequency');
      const existingDur = existingForB.filter((o) => o.type === 'duration');

      /* The count column now shows TOTAL episodes, timed ones included, so the number of
       * synthetic tallies to hold is the target minus the timed episodes already there.
       * Without this subtraction, stopping a timer and then touching the grid would see
       * count=1 with no tally behind it, mint one to make up the difference, and the
       * episode would be counted twice.
       *
       * Clamped at zero: typing a number lower than the timed episodes cannot delete
       * them. A timed episode is removed from the occurrence log, where its duration
       * lives too — deleting it from the count column alone would strip the count while
       * leaving the duration behind. */
      const targetTallies = Math.max(0, targetCount - existingDur.length);

      // Frequency: add synthetic for shortfall, remove most-recent for surplus
      if (existingFreq.length < targetTallies) {
        const toAdd = targetTallies - existingFreq.length;
        for (let i = 0; i < toAdd; i++) {
          nextOccurrences.push({
            id: `${++idCounter}-${Math.floor(Math.random() * 1e9)}`,
            behaviorId: behConfig?.id || behName,
            behaviorName: behName,
            severity: behConfig?.severity || 'medium',
            type: 'frequency',
            timestamp: nowIso,
            time: timeLabel,
            duration: null,
            setting: selectedSetting || null,
            notes: ''
          });
        }
      } else if (existingFreq.length > targetTallies) {
        const toRemove = existingFreq.length - targetTallies;
        const sortedFreq = [...existingFreq].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        const removeIds = new Set(sortedFreq.slice(0, toRemove).map((o) => o.id));
        nextOccurrences = nextOccurrences.filter((o) => !removeIds.has(o.id));
      }

      // Duration: replace today's completed intervals if the total changes
      const currentDur = existingDur.reduce((s, o) => s + (o.duration || 0), 0);
      if (currentDur !== targetDuration) {
        const removeIds = new Set(existingDur.map((o) => o.id));
        nextOccurrences = nextOccurrences.filter((o) => !removeIds.has(o.id));
        if (targetDuration > 0) {
          nextOccurrences.push({
            id: `${++idCounter}-${Math.floor(Math.random() * 1e9)}`,
            behaviorId: behConfig?.id || behName,
            behaviorName: behName,
            severity: behConfig?.severity || 'medium',
            type: 'duration',
            timestamp: nowIso,
            time: timeLabel,
            duration: targetDuration,
            setting: selectedSetting || null,
            notes: ''
          });
        }
      }
    }

    setBehaviorOccurrences(nextOccurrences);
    setBehaviorFunctions(nextFunctions);
    // Park every row the occurrence log cannot reproduce: the unnamed ones, and
    // the named ones that ended up with no occurrence (count 0 and duration 0).
    // Without the second group, choosing a behaviour on a fresh row deleted it -
    // it left the pending list before anything existed to derive it from.
    const backed = new Set(nextOccurrences.map((o) => o.behaviorName));
    setPendingGridRows(newSubRows.filter((sr) => !sr.behavior || !backed.has(sr.behavior)));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  // Setting / Context selector — shared across Individual & Grid views. Only
  // rendered when the IEP defines settings for these behaviors. Each recorded
  // occurrence is tagged with the chosen setting (captured at record time), so
  // changing it mid-session correctly attributes later incidents to the new place.
  const settingSelector = settingRequired ? (
    <MainCard contentSX={{ p: { xs: 2, sm: 2.5 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.lighter',
              color: 'primary.main',
              flexShrink: 0
            }}
          >
            <EnvironmentOutlined />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Setting / Context
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Where is this session happening? Each behavior you record is tagged with this setting.
            </Typography>
          </Box>
        </Stack>
        <TextField
          select
          size="small"
          label="Setting"
          value={selectedSetting}
          onChange={(e) => setSelectedSetting(e.target.value)}
          error={!selectedSetting}
          helperText={!selectedSetting ? 'Select a setting before recording' : ' '}
          sx={{
            minWidth: { xs: '100%', sm: 260 },
            // Make the chosen value clearly legible (default select text rendered thin/grey)
            '& .MuiSelect-select': {
              fontSize: '0.9rem',
              fontWeight: 500,
              color: 'text.primary'
            }
          }}
        >
          {availableSettings.map((s) => (
            <MenuItem key={s} value={s} sx={{ fontSize: '0.9rem' }}>
              {s}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </MainCard>
  ) : null;

  // Empty state — no behaviors configured
  if (activeBehaviors.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">No behaviors are configured for tracking. Please set up behavior targets first.</Alert>
      </Box>
    );
  }

  // Individual View - Quick Tracking Interface
  if (viewMode === 'individual') {
    return (
      <Box sx={isOfflineMode ? { p: { xs: 2, sm: 3 } } : {}}>
        {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
        {!isOfflineMode && !isOnline && (
          <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
            <Typography variant="body2">{intl.formatMessage({ id: 'form.msg.offlineNote' })}</Typography>
          </Alert>
        )}

        {/* Offline Mode Header */}
        {isOfflineMode && (
          <MainCard sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="text" startIcon={<LeftOutlined />} onClick={onBack}>
                  Back
                </Button>
                <Box>
                  <Typography variant="h5">Behavior Tracking - {childName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Offline Mode
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                startIcon={<SaveOutlined />}
                onClick={handleOfflineSave}
                disabled={saving || behaviorOccurrences.filter((o) => !o.active).length === 0}
              >
                {saving ? 'Saving...' : 'Save Offline'}
              </Button>
            </Stack>
          </MainCard>
        )}

        <Stack spacing={3}>
          {/* Setting / Context selector */}
          {settingSelector}

          {/* Behavior Tracking Cards */}
          <Grid container spacing={3}>
            {activeBehaviors.map((behavior) => {
              const frequencyCount = getFrequencyCount(behavior.id);
              const totalDuration = getTotalDuration(behavior.id);
              const hasFrequency = behavior.tracking.includes('frequency');
              const hasDuration = behavior.tracking.includes('duration');
              const timerActive = isTimerActive(behavior.id);
              const durationIntervals = getDurationIntervals(behavior.id);
              const isBreakdownExpanded = expandedBreakdown === behavior.id;

              return (
                <Grid size={{ xs: 12, md: 6 }} key={behavior.id}>
                  <Card
                    sx={{
                      height: '100%',
                      border: 2,
                      borderColor: timerActive ? 'error.main' : 'divider',
                      bgcolor: timerActive ? 'error.lighter' : 'background.paper',
                      transition: 'all 0.2s'
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack spacing={2}>
                        {/* Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Stack spacing={0.5} sx={{ flex: 1 }}>
                            <Typography variant="h5" fontWeight={700}>
                              {behavior.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {behavior.description}
                            </Typography>
                          </Stack>
                          <Chip label={behavior.severity} size="small" color={getSeverityColor(behavior.severity)} variant="filled" />
                        </Stack>

                        {/* Statistics */}
                        <Paper sx={{ p: 2, bgcolor: timerActive ? 'background.paper' : 'grey.50' }}>
                          <Grid container spacing={2}>
                            {hasFrequency && (
                              <Grid size={{ xs: hasDuration ? 6 : 12 }}>
                                <Stack spacing={0.5}>
                                  <Typography variant="h3" fontWeight={700} color="primary.main">
                                    {frequencyCount}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({ id: 'behaviorTracking.frequencyCount' })}
                                  </Typography>
                                </Stack>
                              </Grid>
                            )}
                            {hasDuration && (
                              <Grid size={{ xs: hasFrequency ? 6 : 12 }}>
                                <Stack spacing={0.5}>
                                  <Typography variant="h3" fontWeight={700} color="error.main">
                                    {timerActive ? formatDuration(durationTimer) : formatDuration(totalDuration)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {timerActive ? 'Recording...' : intl.formatMessage({ id: 'behaviorTracking.totalDuration' })}
                                  </Typography>
                                </Stack>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={2}>
                          {hasFrequency && (
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              size="large"
                              startIcon={<PlusOutlined />}
                              onClick={() => handleFrequencyClick(behavior)}
                              disabled={settingRequired && !selectedSetting}
                              sx={{ py: 2 }}
                            >
                              Count
                            </Button>
                          )}
                          {hasDuration && (
                            <Button
                              variant="contained"
                              color={timerActive ? 'error' : 'warning'}
                              fullWidth
                              size="large"
                              startIcon={timerActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                              onClick={() => (timerActive ? handleStopDuration(activeDuration) : handleStartDuration(behavior))}
                              disabled={(activeDuration !== null && !timerActive) || (settingRequired && !selectedSetting && !timerActive)}
                              sx={{ py: 2 }}
                            >
                              {timerActive ? 'Stop' : 'Timer'}
                            </Button>
                          )}
                        </Stack>

                        {/* View Breakdown - Show duration intervals */}
                        {hasDuration && durationIntervals.length > 0 && (
                          <Box>
                            <Button
                              variant="text"
                              size="small"
                              fullWidth
                              onClick={() => setExpandedBreakdown(isBreakdownExpanded ? null : behavior.id)}
                              endIcon={isBreakdownExpanded ? <UpOutlined /> : <DownOutlined />}
                              sx={{ color: 'text.secondary' }}
                            >
                              View Breakdown ({durationIntervals.length} intervals)
                            </Button>
                            {isBreakdownExpanded && (
                              <Paper sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                                <Stack spacing={1}>
                                  {durationIntervals.map((interval, index) => (
                                    <Stack
                                      key={interval.id}
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      sx={{ px: 1, py: 0.5, bgcolor: 'background.paper', borderRadius: 1 }}
                                    >
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="caption" color="text.secondary">
                                          #{durationIntervals.length - index}
                                        </Typography>
                                        <Typography variant="body2">{interval.time}</Typography>
                                      </Stack>
                                      <Chip label={formatDuration(interval.duration)} size="small" color="error" variant="outlined" />
                                    </Stack>
                                  ))}
                                </Stack>
                              </Paper>
                            )}
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Tabs Section */}
          <MainCard>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label={`Recent Occurrences (${behaviorOccurrences.length})`} />
              <Tab label={intl.formatMessage({ id: 'behaviorTracking.behaviorSummary' })} />
              <Tab label={intl.formatMessage({ id: 'behaviorTracking.chronologicalLog' })} />
            </Tabs>

            <Box sx={{ pt: 3 }}>
              {/* Tab 0: Recent Occurrences */}
              {tabValue === 0 && (
                <>
                  {behaviorOccurrences.length === 0 ? (
                    <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                      <WarningOutlined style={{ fontSize: '3rem', color: theme.palette.grey[300] }} />
                      <Typography variant="h6" color="text.secondary">
                        No behaviors recorded yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Click on tracking buttons above to record behaviors
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={1.5}>
                      {behaviorOccurrences.map((occurrence) => (
                        <Paper key={occurrence.id} sx={{ p: 2, border: 1, borderColor: occurrence.active ? 'error.main' : 'divider' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: 1,
                                  bgcolor: occurrence.active ? 'error.lighter' : `${getSeverityColor(occurrence.severity)}.lighter`,
                                  color: occurrence.active ? 'error.main' : `${getSeverityColor(occurrence.severity)}.main`
                                }}
                              >
                                {occurrence.type === 'frequency' ? <PlusOutlined /> : <ClockCircleOutlined />}
                              </Box>
                              <Stack sx={{ flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {occurrence.behaviorName}
                                  </Typography>
                                  <Chip
                                    label={occurrence.type === 'frequency' ? 'Frequency' : 'Duration'}
                                    size="small"
                                    color={occurrence.type === 'frequency' ? 'primary' : 'error'}
                                    variant="outlined"
                                  />
                                  {occurrence.active && <Chip label="ACTIVE" size="small" color="error" variant="filled" />}
                                  {occurrence.setting && (
                                    <Chip
                                      icon={<EnvironmentOutlined />}
                                      label={occurrence.setting}
                                      size="small"
                                      variant="outlined"
                                      sx={{ '& .MuiChip-icon': { fontSize: 12 } }}
                                    />
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  {occurrence.time}
                                  {occurrence.type === 'duration' && !occurrence.active && ` • ${formatDuration(occurrence.duration)}`}
                                  {occurrence.active && ` • ${formatDuration(durationTimer)}`}
                                </Typography>
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder={intl.formatMessage({ id: 'behaviorTracking.addNotesPlaceholder' })}
                                  value={occurrence.notes}
                                  onChange={(e) => handleUpdateNotes(occurrence.id, e.target.value)}
                                  sx={{ mt: 1 }}
                                />
                              </Stack>
                            </Stack>
                            {!occurrence.active && (
                              <IconButton size="small" color="error" onClick={() => handleRemoveOccurrence(occurrence.id)}>
                                <DeleteOutlined />
                              </IconButton>
                            )}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </>
              )}

              {/* Tab 1: Behavior Summary */}
              {tabValue === 1 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'dataCollection.field.behavior' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'behaviorTracking.severity' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'behaviorTracking.frequencyCount' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'behaviorTracking.totalDuration' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'behaviorTracking.lastOccurrence' })}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeBehaviors.map((behavior, index) => {
                        const frequencyCount = getFrequencyCount(behavior.id);
                        const totalDuration = getTotalDuration(behavior.id);
                        const lastOccurrence = behaviorOccurrences.find((o) => o.behaviorId === behavior.id);

                        return (
                          <TableRow key={behavior.id} hover>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {behavior.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {behavior.description}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip label={behavior.severity} size="small" color={getSeverityColor(behavior.severity)} variant="filled" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={frequencyCount}
                                size="small"
                                color={frequencyCount > 0 ? 'primary' : 'default'}
                                variant={frequencyCount > 0 ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {formatDuration(totalDuration)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {lastOccurrence ? lastOccurrence.time : '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Tab 2: Chronological Log */}
              {tabValue === 2 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width={100}>{intl.formatMessage({ id: 'behaviorTracking.time' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'dataCollection.field.behavior' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'behaviorTracking.type' })}</TableCell>
                        <TableCell width={100}>{intl.formatMessage({ id: 'dataCollection.field.duration' })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: 'behaviorTracking.notes' })}</TableCell>
                        <TableCell width={80}>{intl.formatMessage({ id: 'behaviorTracking.actions' })}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {behaviorOccurrences.filter((o) => !o.active).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                              <WarningOutlined style={{ fontSize: '2rem', color: theme.palette.grey[300] }} />
                              <Typography variant="body2" color="text.secondary">
                                No behaviors recorded yet
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ) : (
                        behaviorOccurrences
                          .filter((o) => !o.active)
                          .map((occurrence) => (
                            <TableRow key={occurrence.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {occurrence.time}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {occurrence.behaviorName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={occurrence.type === 'frequency' ? 'Frequency' : 'Duration'}
                                  size="small"
                                  color={occurrence.type === 'frequency' ? 'primary' : 'error'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {occurrence.type === 'duration' ? formatDuration(occurrence.duration) : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder={intl.formatMessage({ id: 'behaviorTracking.addNotesPlaceholder' })}
                                  value={occurrence.notes}
                                  onChange={(e) => handleUpdateNotes(occurrence.id, e.target.value)}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton size="small" color="error" onClick={() => handleRemoveOccurrence(occurrence.id)}>
                                  <DeleteOutlined />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </MainCard>

          {/* Session Notes - Offline Mode Only */}
          {isOfflineMode && (
            <MainCard>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {intl.formatMessage({ id: 'behaviorTracking.sessionNotes' })}
              </Typography>
              <SpeechTextField
                fullWidth
                multiline
                rows={3}
                placeholder={intl.formatMessage({ id: 'behaviorTracking.sessionNotesPlaceholder' })}
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
              />
            </MainCard>
          )}
        </Stack>
      </Box>
    );
  }

  // Grid View - Summary Table with Tabs
  return (
    <Stack spacing={3}>
      {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
      {!isOfflineMode && !isOnline && (
        <Alert severity="info" icon={<DisconnectOutlined />}>
          <Typography variant="body2">{intl.formatMessage({ id: 'form.msg.offlineNote' })}</Typography>
        </Alert>
      )}

      {/* Setting / Context selector */}
      {settingSelector}

      {/* Summary Statistics */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="primary.main">
                {behaviorOccurrences.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Occurrences
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="info.main">
                {behaviorOccurrences.filter((o) => o.type === 'frequency').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Frequency Events
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="error.main">
                {behaviorOccurrences.filter((o) => o.type === 'duration' && !o.active).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Duration Events
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MainCard>
            <Stack spacing={0.5}>
              <Typography variant="h3" fontWeight={700} color="warning.main">
                {formatDuration(
                  behaviorOccurrences.filter((o) => o.type === 'duration' && !o.active).reduce((sum, o) => sum + (o.duration || 0), 0)
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'behaviorTracking.totalDuration' })}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
      </Grid>

      {/* Tabs for Data Entry and Historical Data */}
      <MainCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={intl.formatMessage({ id: 'form.tab.dataEntry' })} />
            <Tab label={intl.formatMessage({ id: 'form.tab.historicalData' })} />
          </Tabs>
          <Stack direction="row" spacing={1} sx={{ pr: 2 }}>
            {tabValue === 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PlusOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  // Today's row is now derived — clicking "New" adds a pending
                  // empty sub-row for the user to assign a behavior to.
                  setPendingGridRows((old) => [...old, { behavior: '', function: '', count: 0, duration: 0 }]);
                }}
              >
                New
              </Button>
            )}
            <CSVExport
              data={behaviorTrackingData.flatMap((d) => [d, ...(d.subRows || [])]).filter((row) => row.behavior)}
              headers={[
                { label: 'Date', key: 'date' },
                { label: 'Behavior', key: 'behavior' },
                { label: 'Function', key: 'function' },
                { label: 'Count', key: 'count' },
                { label: 'Duration (seconds)', key: 'duration' }
              ]}
              filename="behavior-tracking.csv"
            />
          </Stack>
        </Stack>

        <Box sx={{ pt: 3 }}>
          {/* Tab 0: Data Entry — today's row is DERIVED from behaviorOccurrences,
              so Individual-mode entries show here automatically. Edits flow back
              through reconcileTodayRow to mutate occurrences. */}
          {tabValue === 0 && (
            <BehaviorTrackingTable
              data={[computeTodayRow()]}
              setData={(updateFn) => {
                const oldData = [computeTodayRow()];
                const newData = typeof updateFn === 'function' ? updateFn(oldData) : updateFn;
                const newTodayRow = newData.find((d) => isToday(d.date)) || newData[0];
                if (newTodayRow) reconcileTodayRow(newTodayRow);
              }}
              behaviors={activeBehaviors}
              dataEntryMode={true}
            />
          )}

          {/* Tab 1: Historical Data — read-only. Prepends today's derived row
              so users browsing history still see today's totals. Non-today
              edits (none in read-only) would pass through to behaviorTrackingData. */}
          {tabValue === 1 && (
            <BehaviorTrackingTable
              data={[computeTodayRow(), ...historicalRows.filter((d) => !isToday(d.date))]}
              setData={(updateFn) => {
                const oldData = [computeTodayRow(), ...historicalRows.filter((d) => !isToday(d.date))];
                const newData = typeof updateFn === 'function' ? updateFn(oldData) : updateFn;
                setBehaviorTrackingData(newData.filter((d) => !isToday(d.date)));
              }}
              behaviors={activeBehaviors}
              dataEntryMode={false}
            />
          )}
        </Box>
      </MainCard>
    </Stack>
  );
};

BehaviorTrackingMode.propTypes = {
  viewMode: PropTypes.string,
  onDataChange: PropTypes.func,
  behaviors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      tracking: PropTypes.arrayOf(PropTypes.string),
      description: PropTypes.string,
      severity: PropTypes.string,
      settings: PropTypes.string
    })
  ),
  // Offline mode props
  isOfflineMode: PropTypes.bool,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string,
  onSave: PropTypes.func,
  onBack: PropTypes.func,
  onTimerStateChange: PropTypes.func
};

export default BehaviorTrackingMode;
